import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, fmtTime } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clock, LogIn, LogOut, Plus, Trash2, CalendarDays, Wallet } from "lucide-react";

export default function Attendance() {
  const qc = useQueryClient();
  const month = new Date().toISOString().slice(0, 7);
  const today = new Date().toISOString().slice(0, 10);
  const [shiftOpen, setShiftOpen] = useState(false);
  const [shiftForm, setShiftForm] = useState({ staff_id: "", date: today, start: "09:00", end: "17:00", role: "Cashier" });
  const [wages, setWages] = useState({});

  const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: async () => (await api.get("/auth/staff")).data });
  const { data: attendance = [] } = useQuery({ queryKey: ["attendance", today], queryFn: async () => (await api.get(`/attendance?date=${today}`)).data, refetchInterval: 15000 });
  const { data: shifts = [] } = useQuery({ queryKey: ["shifts"], queryFn: async () => (await api.get("/shifts")).data });
  const { data: payroll } = useQuery({ queryKey: ["payroll", month], queryFn: async () => (await api.get(`/payroll?month=${month}`)).data });

  const attFor = (id) => attendance.find((a) => a.staff_id === id);

  const toggle = async (s) => {
    const { data } = await api.post("/attendance/toggle", { staff_id: s.id, staff_name: s.name });
    toast.success(data.action === "clock_in" ? `${s.name} clocked in` : `${s.name} clocked out (${data.hours}h)`);
    qc.invalidateQueries({ queryKey: ["attendance", today] });
  };
  const addShift = async () => {
    const st = staff.find((x) => x.id === shiftForm.staff_id);
    if (!st) return toast.error("Select staff");
    await api.post("/shifts", { ...shiftForm, staff_name: st.name });
    toast.success("Shift scheduled"); setShiftOpen(false);
    qc.invalidateQueries({ queryKey: ["shifts"] });
  };
  const delShift = async (s) => { await api.delete(`/shifts/${s.id}`); qc.invalidateQueries({ queryKey: ["shifts"] }); };
  const saveWage = async (id) => {
    await api.put(`/staff/${id}/wage`, { hourly_rate: Number(wages[id] || 0) });
    toast.success("Wage updated"); qc.invalidateQueries({ queryKey: ["payroll", month] });
  };

  return (
    <div className="h-full overflow-y-auto p-6 lg:p-8">
      <PageHeader title="Staff Attendance & Payroll" subtitle="Clock-in/out, shifts and payroll — Attendo module" />
      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="shifts" data-testid="tab-shifts">Shifts</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Payroll</TabsTrigger>
        </TabsList>

        <TabsContent value="attendance">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {staff.map((s) => {
              const a = attFor(s.id);
              const clockedIn = a && !a.clock_out;
              return (
                <div key={s.id} className="kul-card p-4" data-testid={`att-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[var(--kul-primary)]/10 text-[var(--kul-primary)] flex items-center justify-center font-semibold">{s.name?.[0]?.toUpperCase()}</div>
                    <div className="flex-1"><div className="font-semibold">{s.name}</div><div className="text-xs text-neutral-400 capitalize">{s.role}</div></div>
                    <Badge className={clockedIn ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-500"}>{clockedIn ? "In" : a ? "Out" : "—"}</Badge>
                  </div>
                  {a && <div className="text-xs text-neutral-500 mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> In {fmtTime(a.clock_in)} {a.clock_out ? `· Out ${fmtTime(a.clock_out)} · ${a.hours}h` : ""}</div>}
                  <Button size="sm" onClick={() => toggle(s)} data-testid={`clock-${s.id}`}
                    className={`w-full mt-3 ${clockedIn ? "bg-[var(--kul-danger)] hover:bg-red-700" : "bg-[var(--kul-success)] hover:bg-emerald-600"} text-white`}>
                    {clockedIn ? <><LogOut className="w-4 h-4 mr-1" /> Clock Out</> : <><LogIn className="w-4 h-4 mr-1" /> Clock In</>}
                  </Button>
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="shifts">
          <div className="flex justify-end mb-3"><Button onClick={() => setShiftOpen(true)} data-testid="add-shift-btn" className="bg-[var(--kul-primary)] text-white"><Plus className="w-4 h-4 mr-1" /> Schedule Shift</Button></div>
          <div className="kul-card overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-neutral-50"><TableHead>Staff</TableHead><TableHead>Date</TableHead><TableHead>Timing</TableHead><TableHead>Role</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {shifts.map((s) => (
                  <TableRow key={s.id} data-testid={`shift-${s.id}`}>
                    <TableCell className="font-medium">{s.staff_name}</TableCell>
                    <TableCell className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5 text-neutral-400" /> {s.date}</TableCell>
                    <TableCell className="font-mono-k">{s.start} – {s.end}</TableCell>
                    <TableCell>{s.role}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-[var(--kul-danger)]" onClick={() => delShift(s)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!shifts.length && <p className="text-neutral-400 text-center py-10">No shifts scheduled.</p>}
          </div>
        </TabsContent>

        <TabsContent value="payroll">
          <div className="kul-card overflow-hidden">
            <div className="p-4 border-b flex items-center gap-2 text-sm text-neutral-500"><Wallet className="w-4 h-4" /> Payroll for {payroll?.month} · based on clocked hours</div>
            <Table>
              <TableHeader><TableRow className="bg-neutral-50"><TableHead>Staff</TableHead><TableHead className="text-right">Days</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Rate/hr</TableHead><TableHead className="text-right">Gross Pay</TableHead></TableRow></TableHeader>
              <TableBody>
                {(payroll?.rows || []).map((r) => (
                  <TableRow key={r.staff_id} data-testid={`pay-${r.staff_id}`}>
                    <TableCell className="font-medium">{r.name} <span className="text-xs text-neutral-400 capitalize">({r.role})</span></TableCell>
                    <TableCell className="text-right font-mono-k">{r.days_present}</TableCell>
                    <TableCell className="text-right font-mono-k">{r.total_hours}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Input type="number" defaultValue={r.hourly_rate} onChange={(e) => setWages({ ...wages, [r.staff_id]: e.target.value })} className="h-8 w-20 text-right" data-testid={`wage-${r.staff_id}`} />
                        <Button size="sm" variant="outline" className="h-8" onClick={() => saveWage(r.staff_id)}>Set</Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono-k font-semibold">{money(r.gross)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={shiftOpen} onOpenChange={setShiftOpen}>
        <DialogContent><DialogHeader><DialogTitle>Schedule Shift</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Staff</Label><select value={shiftForm.staff_id} onChange={(e) => setShiftForm({ ...shiftForm, staff_id: e.target.value })} className="w-full h-10 rounded-lg border px-2 text-sm" data-testid="shift-staff"><option value="">Select…</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Date</Label><Input type="date" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} /></div>
              <div><Label>Start</Label><Input type="time" value={shiftForm.start} onChange={(e) => setShiftForm({ ...shiftForm, start: e.target.value })} /></div>
              <div><Label>End</Label><Input type="time" value={shiftForm.end} onChange={(e) => setShiftForm({ ...shiftForm, end: e.target.value })} /></div>
            </div>
            <div><Label>Role</Label><Input value={shiftForm.role} onChange={(e) => setShiftForm({ ...shiftForm, role: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addShift} className="bg-[var(--kul-primary)] text-white" data-testid="save-shift">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
